<template>
    <v-card class="chart">
        <v-card-title>
            <v-btn class="mr-2" small @click="showFlag=!showFlag">
            <v-icon v-if="showFlag">mdi-chevron-down</v-icon>
            <v-icon v-else>mdi-chevron-right</v-icon>
            </v-btn>
            <div :style="`color:${isDarkMode ? 'white': 'black'}`">Sync Status</div>
            <v-spacer />
            <v-tooltip left>
                <template v-slot:activator="{ on }">
                <v-icon class="mr-1" v-on="on" :color="syncConnectionIsOpen ? 'green':'red'">mdi-hubspot</v-icon>
                </template>
                <span v-if="syncConnectionIsOpen">Sync Hub is Connected</span>
                <span v-else>Sync Hub is NOT Connected</span>
            </v-tooltip>
        </v-card-title>
        <v-card-text :style="`display:${showFlag?'':'none'}`">
            <apexchart ref="theChart" type="heatmap" @dataPointSelection="dataPointSelection" :options="chartOptions" :series="series" />
        </v-card-text>
    </v-card>
</template>

<script>
// This component uses ApexCharts. See documentation at
// https://apexcharts.com/docs/vue-charts/

import Router from '../router';
import { mapState } from "vuex";
import signalr from '../signalr/signalr.js';

const tabletState = { good: 0, queued: 1, syncing: 2, conflict: 3, failed: 4, expired: 5, disabled: 6 };
export default {  
    data:() => ({
        showFlag: true,
        syncConnection: null,
        syncConnectionIsOpen: false,
        tablets: [],
        series: [],
        syncTablets: [],
        chartOptions: {
            theme: {},
            chart: {
                height:600,
                type: 'heatmap',
                zoom: { enabled: false }
            },
            plotOptions: {
                heatmap: {
                    colorScale: {}
                }
            },
            dataLabels: {
                enabled: true,
            },
            labels: [],
            tooltip: {},
            stroke: {
                width: 1
            },
            title: {
                text: ''
            },
        },
    }),

    async beforeDestroy() {
        if (this.syncConnectionIsOpen) {
            await this.syncConnection.send('RemoveFromDashboardGroup');
            await this.closeSyncConnection();
        }
    },

    async created() {
        var queueTablets;
        this.chartOptions.theme.mode = this.isDarkMode ? 'dark' : '';
        
        try {
            await this.openSyncConnection();
            queueTablets = await this.syncConnection.invoke('GetSyncQueue');
            this.syncTablets = await this.syncConnection.invoke('GetSyncSummary');
           
            queueTablets.forEach(qt => {
                var st = this.syncTablets.find(t => t.tabletName === qt.tabletName);

                if (st == null)
                    this.syncTablets.push( {tabletName: qt.tabletName, syncStatus: 'Queued'} );
                else st.syncStatus = 'Queued';
            });

            this.$nextTick(() => this.checkForExpiredSyncs());
        }
        catch (err) {
            this.$root.showNoticeError(`Connection to Sync Hub failed: ${err}`);
            return;
        }
    },

    computed: {
        ...mapState(["settings", "tabletInfo"]),
        theChart() {
            return this.$refs.theChart;
        },
        isDarkMode() {
            var mode = this.settings.isDarkMode;
            return mode;
        }
    },
    watch: {
        syncTablets: {
            async handler(newVal, oldValue) {
                if(newVal === oldValue) return;

                this.initializeChart();

                this.tablets.forEach(t => {
                        this.setTabletSyncState(newVal, t);
                });

                this.theChart.updateOptions(this.chartOptions);
                this.updateChart();
            }
        }
    },
    methods: {
        checkForExpiredSyncs() {
            var updateFlag = false;

            if (this.tablets === null || this.tablets === undefined) return;
            
            this.tablets.forEach(t => {
                if (t.state == tabletState.good || t.state == tabletState.conflict) {
                    if (this.isSyncExpired(t.lastSync)) {
                        t.state = tabletState.expired;
                        updateFlag = true;
                    }
                }
            });
            if (updateFlag) this.updateChart();

            const fiveMinutes = 1000 * 60 * 0.25;
            setTimeout(() => { this.checkForExpiredSyncs(); }, fiveMinutes); 
        },
        setTabletSyncState(syncTablets, t)
        {
            var syncTablet = syncTablets.find(s => s.tabletName === t.name);

            if (!syncTablet) return;
            
            switch(syncTablet.syncStatus) {
                case 'Disabled': {
                    t.state = tabletState.disabled;
                    break;
                }
                case 'Queued': {
                    t.state = tabletState.queued;
                    break; 
                }
                case 'Syncing': {
                    t.state = tabletState.syncing;
                    break;
                }
                case 'Ready': {
                    t.numberOfConflicts = syncTablet.numberOfConflicts;
                    t.lastSync = syncTablet.lastSyncTime;
                    // if (this.isSyncNeeded(t.lastSync))
                    //     t.state = tabletState.expired;
                    // else
                    if (t.numberOfConflicts === 0 || t.numberOfConflicts === null)
                        t.state = tabletState.good;
                    else {
                        t.state = tabletState.conflict;
                    }
                    break;
                }
                default: { // 'Failed'
                    t.state = tabletState.failed;
                    break;
                }
            }
        },
        initializeChart() {
            this.series = [];
            this.chartOptions.labels = [];

            this.tablets = this.tabletInfo;

            if(this.tablets === null || this.tablets === undefined) return;

            this.chartOptions.plotOptions.heatmap.colorScale.ranges = this.getConflictStatus();

            this.chartOptions.tooltip.custom = this.tooltipFormatter;
            this.chartOptions.dataLabels.formatter = this.dataLabelFormatter;
            
            var tsTabletCnt = this.tablets.length;
            if (tsTabletCnt == 0) return;

            var farmNames = [this.tablets[0].farmCode];
            var tabletNames = [];
            var allTablets = [];
            var innerArrayValue = [];
            var innerArrayName = [];
            var latestFarmCode = farmNames[0];

            for (var i=0; i< tsTabletCnt; i++) {
                if (this.tablets[i].farmCode != latestFarmCode) {
                    allTablets.push(innerArrayValue);
                    tabletNames.push(innerArrayName);
                    innerArrayValue = [];
                    innerArrayName = [];
                    latestFarmCode = this.tablets[i].farmCode;
                    farmNames.push(latestFarmCode);
                }
                innerArrayValue.push(tabletState.good);
                innerArrayName.push(this.tablets[i].name);
            }
            if (innerArrayValue.length > 0) {
                allTablets.push(innerArrayValue);
                tabletNames.push(innerArrayName);
            }

            this.chartOptions.tooltip.tabletNames = tabletNames;

            var maxFarmTabletCnt = 0;
            farmNames.forEach((name, index) => {
                var farmTabletCnt = allTablets[index].length;
                maxFarmTabletCnt =  farmTabletCnt > maxFarmTabletCnt ? farmTabletCnt : maxFarmTabletCnt;
                this.series.push( { name: name, data: allTablets[index] });
            });
            for (var j=0; j< maxFarmTabletCnt; j++) this.chartOptions.labels.push(j+1);
        },
        async openSyncConnection() {
            this.syncConnection = await signalr.buildHub();

            try {
                this.syncConnection.on('ClientConnected', () => {}); // Ignore
                this.syncConnection.on('ClientDisconnected', () => {}); // Ignore
                this.syncConnection.on('TabletConnected', () => {}); // Ignore
                this.syncConnection.on('TabletDisconnected', () => {}); // Ignore
                this.syncConnection.on('SyncQueued', 
                    data => {
                        var tablet = this.tablets.find(t => t.name ===data.tabletName);
                        if (tablet) {
                            tablet.state = tabletState.queued;
                            this.updateChart();
                        }
                    }
                );
                this.syncConnection.on('SyncStarted', 
                    data => {
                        this.$root.showNoticeInfo(`Sync STARTED for '${data.tabletName}'.`);
                        var tablet = this.tablets.find(t => t.name ===data.tabletName);
                        if (tablet) {
                            tablet.state = tabletState.syncing;
                            tablet.lastSync = data.lastSyncTime;
                            this.updateChart();
                        }
                    }
                );
                this.syncConnection.on('SyncCompleted',
                    data => {
                        this.$root.showNoticeInfo(`Sync COMPLETED for '${data.tabletName}'.`);
                        var t = this.tablets.find(s => s.name ===data.tabletName);
                        t.numberOfConflicts = data.numberOfConflicts;
                        t.lastSync = data.lastSyncTime;
                        // if (this.isSyncNeeded(t.lastSync))
                        //     t.state = tabletState.expired;
                        // else 
                        if (t.numberOfConflicts === 0)
                            t.state = tabletState.good;
                        else {
                            t.state = tabletState.conflict;
                        }

                        this.updateChart();
                    }
                );
                this.syncConnection.on('SyncFailed',
                    data => {
                        this.$root.showNoticeInfo(`Sync FAILED for '${data.tabletName}'.`);
                        var tablet = this.tablets.find(t => t.name ===data.tabletName);
                        if (tablet) {
                            tablet.state = tabletState.failed;
                            tablet.lastSync = data.lastSyncTime;
                            this.updateChart();
                        }
                    }
                );
                this.syncConnection.on('SyncProgress',  () => {} ); // Ignore
                this.syncConnection.on('TabletEnabled', 
                    data => {
                        this.$root.showNoticeInfo(`Tablet Sync ENABLED for '${data.syncSummary.tabletName}'.`);
                        var tablet = this.tablets.find(t => t.name === data.syncSummary.tabletName);

                        if (tablet) {
                            tablet.state = data.syncSummary.state;
                            tablet.lastSync = data.syncSummary.lastSyncTime;
                            tablet.numberOfConflicts = data.syncSummary.numberOfConflicts;

                            var syncTablets = [];
                            syncTablets.push(data.syncSummary);
                            this.setTabletSyncState(syncTablets, tablet);

                            this.updateChart();
                        }
                    }
                ); 
                this.syncConnection.on('TabletDisabled', 
                    data => {
                        this.$root.showNoticeInfo(`Tablet Sync DISABLED for '${data.tabletName}'.`);
                        var tablet = this.tablets.find(t => t.name === data.tabletName);
                        if (tablet) {
                            tablet.state = tabletState.disabled;
                            this.updateChart();
                        }
                    }
                );

                this.syncConnection.onclose(() => {
                    this.syncConnectionIsOpen = false;
                });

                await this.syncConnection.start();
                this.syncConnectionIsOpen = true;
                await this.syncConnection.send('AddToDashboardGroup');
            }
            catch (err) {
                this.$root.showNoticeError(`Connection to Sync Hub failed: ${err}`);
                this.syncConnectionIsOpen = false;
            }
        },
        async closeSyncConnection() {
            await this.syncConnection.stop();
            this.syncConnectionIsOpen = false;
        },
        updateChart() {
            var chart = this.theChart;

            if (this.chartOptions.tooltip.tabletNames == null) return;
            if (chart == null) return;

            var numFarms = this.chartOptions.tooltip.tabletNames.length;
            for (var i=0; i<numFarms; i++)
            {
                var numFarmTablets = this.chartOptions.tooltip.tabletNames[i].length;
                for (var j=0; j<numFarmTablets; j++)
                {
                    const tabletName = this.chartOptions.tooltip.tabletNames[i][j];
                    const tablet = this.tablets.find(x => x.name === tabletName);
                
                    if (tablet) {
                        this.series[i].data[j] = tablet.state;
                    }
                }
            }

            

            this.theChart.updateSeries(this.series);
        },
        isSyncExpired(lastSyncTime) {
            if (lastSyncTime == null) return false;

            const FOURTEEN_DAYS = 60 * 60 * 24 * 14 * 1000;
            const now = new Date();
            const prev = new Date(lastSyncTime);

            if ((now - prev) > FOURTEEN_DAYS)
                return true;
            else
                return false;
        },
        dataPointSelection(event, chartContext, selected) {
            var name = selected.w.config.tooltip.tabletNames[selected.seriesIndex][selected.dataPointIndex];
            Router.push({name:'TabletConflict',query:{name:name}})
        },
        getConflictStatus() {
            return [
                { from: tabletState.good, to: tabletState.good, name: 'Good',  color: '#05c840' },
                { from: tabletState.queued, to: tabletState.queued, name: 'Queued', color: '#54a9ee'},
                { from: tabletState.syncing, to: tabletState.syncing, name: 'Syncing', color: '#2176bb'},
                { from: tabletState.conflict, to: tabletState.conflict, name: 'Conflict', color: '#ff9807' },
                { from: tabletState.failed, to: tabletState.failed, name: 'Failed', color: '#fa0f0f' },
                { from: tabletState.expired, to: tabletState.expired, name: 'Expired', color: '#f76363'},
                { from: tabletState.disabled, to: tabletState.disabled, name: 'Disabled', color: '#919191'}
            ];
        },
        tooltipFormatter({series, seriesIndex, dataPointIndex, w}) {
            var tabletName = w.config.tooltip.tabletNames[seriesIndex][dataPointIndex];

            var numberOfConflicts = "?";
            var tablet = this.tablets.find(x=>x.name === tabletName);
            if (!tablet) return;

            numberOfConflicts = tablet.numberOfConflicts;
            var desc = "Unknown Sync Status";
            const lastSyncDate =  this.formatDate(tablet.lastSync);

            switch(series[seriesIndex][dataPointIndex]) {
                case tabletState.good: {
                    if (lastSyncDate)
                        desc = `Good: ${numberOfConflicts} Conflicts, Last Sync - ${lastSyncDate}`;   
                    else 
                        desc = 'Good: Last Sync - N/A';
                    break;
                }
                case tabletState.conflict: {
                    desc = `Conflict: ${numberOfConflicts} Conflicts, Last Sync - ${lastSyncDate}`;
                    break;
                }
                case tabletState.syncing: {
                    desc = 'Sync In Progress';
                    break;
                }
                case tabletState.queued: {
                    desc = 'Sync Queued';
                    break;
                }
                case tabletState.expired: {
                    desc = `Expired : ${numberOfConflicts} Conflicts, Last Sync - ${lastSyncDate}`;
                    break;
                }
                case tabletState.failed: {
                    desc = 'Sync Failed';
                    break;
                }
                case tabletState.disabled: {
                    desc = 'Disabled';
                    break;
                }
            }

            var color = this.isDarkMode ? '#dddddd': '#1e1e1e';
            return `<div style="color: ${color}; padding:5px">
                      <span>${tabletName}: ${desc}</span>
                    </div>`;
        },
        dataLabelFormatter(val) {
            if (val === tabletState.good) return "G";
            else if (val === tabletState.conflict) return "C";
            else if (val === tabletState.disabled) return "D";
            else if (val === tabletState.failed) return "F";
            else if (val === tabletState.queued) return "Q";
            else if (val === tabletState.syncing) return "S";
            else if (val === tabletState.expired) return "E";
            else return "U";
        },
        formatDate(dtStr) {
            if (dtStr === null) return "";
            var d = new Date(dtStr);
            var month = this.padNum(d.getMonth()+1, 2);
            var day = this.padNum(d.getDate(), 2);
            var year = this.padNum(d.getFullYear(), 4);
            var date = `${month}/${day}/${year}`;
            return date;
        },
        padNum(num, size) {
            var s = num + "";
            while (s.length < size) s = "0" + s;
            return s;
        }
    }
}
</script>
<style scoped>
    .chart {
        max-width: 600px;
        min-width:600px;
        color:white;
    }
</style>